import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bot, CheckCircle2, Clock3, Gauge, Save, TriangleAlert } from 'lucide-react';
import { toast } from 'react-toastify';
import { api } from '../lib_api';

type Cadence = {
  minutes: number;
  seconds: number;
  minimumMinutes: number;
  maximumMinutes: number;
  recommendedMinutes: number[];
  appliesTo: string;
  effectiveWithoutRestart: boolean;
};

const PRESETS = [1, 3, 5, 10, 15, 30];

function impact(minutes: number) {
  if (minutes <= 1) return { label: 'High frequency', tone: 'text-rose-700 dark:text-rose-300', note: 'Fastest reaction, with the highest AI usage and server load.' };
  if (minutes <= 3) return { label: 'Responsive', tone: 'text-amber-700 dark:text-amber-300', note: 'Faster opportunity detection with increased AI usage.' };
  if (minutes <= 5) return { label: 'Recommended', tone: 'text-emerald-700 dark:text-emerald-300', note: 'Strong balance between responsiveness, cost, and stability.' };
  return { label: 'Economical', tone: 'text-sky-700 dark:text-sky-300', note: 'Lower AI usage, but short-lived setups may be detected later.' };
}

export default function Settings() {
  const queryClient = useQueryClient();
  const cadence = useQuery<Cadence>({
    queryKey: ['admin', 'ai-cadence'],
    queryFn: async () => (await api.get('/api/admin/ai-cadence')).data,
  });
  const [minutes, setMinutes] = useState(5);
  useEffect(() => {
    if (cadence.data?.minutes) setMinutes(cadence.data.minutes);
  }, [cadence.data?.minutes]);
  const save = useMutation({
    mutationFn: async () => (await api.patch('/api/admin/ai-cadence', { minutes })).data as Cadence,
    onSuccess: (data) => {
      queryClient.setQueryData(['admin', 'ai-cadence'], data);
      queryClient.invalidateQueries({ queryKey: ['admin', 'trading-desk-health'] });
      toast.success(`AI Trade Center will now evaluate every ${data.minutes} minute${data.minutes === 1 ? '' : 's'}.`);
    },
    onError: (error: any) => toast.error(error?.response?.data?.error || 'AI cadence could not be updated.'),
  });
  const currentImpact = impact(minutes);
  const unchanged = Number(cadence.data?.minutes) === Number(minutes);

  return (
    <div className="space-y-4">
      <section className="panel overflow-hidden">
        <div className="flex flex-col gap-4 border-b border-gray-200 p-5 dark:border-white/10 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300"><Bot size={22} /></span>
            <div>
              <p className="text-xs font-medium uppercase text-gray-500 dark:text-darkTextSecondary">AI operations</p>
              <h2 className="mt-0.5 text-xl font-semibold text-gray-950 dark:text-white">Market review cadence</h2>
              <p className="mt-1 max-w-2xl text-sm text-gray-600 dark:text-darkTextSecondary">Choose how often every connected AI Trade Center reassesses eligible markets and strategies.</p>
            </div>
          </div>
          <span className="inline-flex items-center gap-2 self-start rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/30"><CheckCircle2 size={14} /> Live global control</span>
        </div>

        <div className="grid gap-6 p-5 lg:grid-cols-[1.35fr_0.65fr]">
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-white">Review every</p>
            <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-6">
              {PRESETS.map((value) => (
                <button key={value} type="button" className={`min-h-12 rounded-lg border px-3 text-sm font-semibold transition ${minutes === value ? 'border-indigo-600 bg-indigo-600 text-white shadow-sm' : 'border-gray-300 bg-white text-gray-800 hover:border-indigo-400 dark:border-white/15 dark:bg-white/5 dark:text-white'}`} onClick={() => setMinutes(value)}>
                  {value} min
                </button>
              ))}
            </div>
            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
              <label className="block max-w-[220px]">
                <span className="text-xs text-gray-500 dark:text-darkTextSecondary">Custom cadence (minutes)</span>
                <input className="input mt-1" type="number" min={cadence.data?.minimumMinutes || 1} max={cadence.data?.maximumMinutes || 30} step="1" value={minutes} onChange={(event) => setMinutes(Number(event.target.value))} />
              </label>
              <button type="button" className="btn min-w-[180px] bg-indigo-600 hover:bg-indigo-700" disabled={save.isPending || cadence.isLoading || unchanged || minutes < 1 || minutes > 30} onClick={() => save.mutate()}>
                <Save size={17} /> {save.isPending ? 'Applying cadence' : 'Apply cadence'}
              </button>
            </div>
            <p className="mt-3 flex items-center gap-2 text-xs text-gray-500 dark:text-darkTextSecondary"><Clock3 size={14} /> Applies on each worker's next runtime loop. No MT5 or backend restart is required.</p>
          </div>

          <aside className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-white/10 dark:bg-white/5">
            <div className="flex items-center gap-2"><Gauge size={18} className={currentImpact.tone} /><p className={`font-semibold ${currentImpact.tone}`}>{currentImpact.label}</p></div>
            <p className="mt-2 text-sm text-gray-600 dark:text-darkTextSecondary">{currentImpact.note}</p>
            <div className="mt-4 border-t border-gray-200 pt-4 dark:border-white/10">
              <p className="text-xs text-gray-500 dark:text-darkTextSecondary">Estimated review opportunities</p>
              <p className="mt-1 text-2xl font-semibold text-gray-950 dark:text-white">{Math.round(60 / Math.max(1, minutes))}<span className="ml-1 text-sm font-normal text-gray-500">per hour</span></p>
            </div>
            {minutes < 3 ? <p className="mt-4 flex items-start gap-2 text-xs text-amber-700 dark:text-amber-300"><TriangleAlert size={15} className="mt-0.5 shrink-0" /> Use this rate only when increased AI cost and VPS load are acceptable.</p> : null}
          </aside>
        </div>
      </section>
    </div>
  );
}
